import React, { useState } from 'react';
import { useFetchSubscriptionPlansQuery } from '../service';
import LoadingIndicator from '../../components/shared/LoadingIndicator/LoadingIndicator';
import { SubscriptionPlans as SubscriptionPlansType, UserSubscription } from '../types';
import { Button, Card, Col, List, Row } from 'antd';
import InvoiceHistory from '../InvoiceHistory';
import SubscriptionPlansHeader from './SubscriptionPlansHeader';
import { getPlanSettings } from '../utils';
import useUpdateSubscription from '../hooks/useUpdateSubscription';
import CancellationModal from '../CancellationModal';
import useCancelSubscription from '../hooks/useCancelSubscription';
import { useNavigate } from "react-router-dom";

type Props = {
    userSubscription?: UserSubscription;
    selectedPlanCode: string;
    isSubscriptionFreeTrial?: boolean;
    includeHeader?: boolean;
    isInFreeTrialSetup?: boolean;
    onSelectSubscription?: (planCode: string) => void;
};

export default function SubscriptionPlans({
    userSubscription,
    selectedPlanCode,
    isSubscriptionFreeTrial,
    includeHeader,
    isInFreeTrialSetup,
    onSelectSubscription = () => { },
}: Props) {
    const navigate = useNavigate();
    const [selectedIntervalUnit, setSelectedIntervalUnit] = useState<keyof SubscriptionPlansType>('monthly');
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

    const { data: plans, isLoading: isLoadingPlans } = useFetchSubscriptionPlansQuery();
    const { updateSubscription, updatedPlanCode, isUpdatingSubscription } = useUpdateSubscription();
    const { cancelSubscription, isCancellingSubscription } = useCancelSubscription(!userSubscription);

    const handlePlanClicked = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        const plan = event.currentTarget.getAttribute('data-plan-code');
        const action = event.currentTarget.getAttribute('data-plan-action');

        if (!plan) {
            return;
        }

        if (action === 'select') {
            onSelectSubscription(plan);
        }
        if (action === 'cancel') {
            setIsModalVisible(true);
        }

        if (action === 'update') {
            updateSubscription(plan);
        }

        if (action === 'checkout') {
            navigate(`/subscription/checkout/${plan}`);
        }
    };

    return (
        <>
            {isLoadingPlans && <LoadingIndicator />}

            {!isLoadingPlans && plans && (
                <>
                    {isModalVisible && (
                        <CancellationModal
                            title="Confirm subscription cancellation"
                            showInfo={true}
                            showFreeTrialInfo={isSubscriptionFreeTrial}
                            isLoading={isCancellingSubscription}
                            onConfirmCancellation={cancelSubscription}
                            onClose={() => setIsModalVisible(false)}
                        />
                    )}

                    <Row
                        gutter={16}
                        className="subscription-plans"
                    >
                        <Col span={isInFreeTrialSetup ? 24 : 18}>
                            <div className="plans-container">
                                <Card
                                    className="plans-card"
                                    title={isInFreeTrialSetup ? '' : 'Select your plan'}
                                    bordered={isInFreeTrialSetup ? false : true}
                                >
                                    <Row className="plan-switch-container">
                                        <Col
                                            span={8}
                                            offset={8}
                                        >
                                            <Button.Group
                                                className="plan-switch-group"
                                                size="large"
                                            >
                                                <Button
                                                    className={`plan-switch ${selectedIntervalUnit === 'monthly' ? 'selected' : ''}`}
                                                    data-interval-unit="monthly"
                                                    onClick={() => setSelectedIntervalUnit('monthly')}
                                                >
                                                    Monthly
                                                </Button>
                                                <Button
                                                    className={`plan-switch ${selectedIntervalUnit === 'yearly' ? 'selected' : ''}`}
                                                    data-interval-unit="yearly"
                                                    onClick={() => setSelectedIntervalUnit('yearly')}
                                                >
                                                    Yearly
                                                </Button>
                                            </Button.Group>
                                        </Col>
                                    </Row>

                                    <Row
                                        className="packages flexbox custom-scrollbar"
                                        gutter={16}
                                    >
                                        {plans[selectedIntervalUnit].map(
                                            (plan) =>
                                                !plan.settings.legacy && (
                                                    <Col
                                                        className="gutter-row plan-items"
                                                        key={plan.code}
                                                        span={8}
                                                    >
                                                        <List
                                                            className="plan-item-list"
                                                            size="large"
                                                            bordered={false}
                                                            dataSource={getPlanSettings(plan)}
                                                            renderItem={(item) => (
                                                                <PlanSettingsItem
                                                                    itemLabel={item.name}
                                                                    itemValue={item.value}
                                                                />
                                                            )}
                                                            header={
                                                                <PlanSettingsHeader
                                                                    name={plan.name}
                                                                    price={plan.price}
                                                                    billingPeriod={plan.billing_period}
                                                                />
                                                            }
                                                            footer={
                                                                <PlanSettingsFooter
                                                                    planCode={plan.code}
                                                                    isLoading={isUpdatingSubscription && updatedPlanCode === plan.code}
                                                                    hasActiveSubscription={userSubscription?.status === 'active'}
                                                                    isThisPlanSelected={selectedPlanCode === plan.code}
                                                                    isInFreeTrialSetup={isInFreeTrialSetup}
                                                                    onPlanClicked={handlePlanClicked}
                                                                />
                                                            }
                                                        />
                                                    </Col>
                                                )
                                        )}
                                    </Row>
                                </Card>
                            </div>
                        </Col>

                        {!isInFreeTrialSetup && (
                            <Col span={6}>
                                <div className="pricing-meta">
                                    {includeHeader && userSubscription && (
                                        <SubscriptionPlansHeader
                                            subscriptionStatus={userSubscription.status}
                                            planName={userSubscription.plan_details.name}
                                            planPrice={userSubscription.plan_details.price}
                                            planIntervalUnit={userSubscription.plan_details.interval_unit}
                                            planIntervalLength={userSubscription.plan_details.interval_length}
                                            currentPeriodEnd={userSubscription.current_period_ends_at}
                                            isInFreeTrial={isSubscriptionFreeTrial}
                                        />
                                    )}

                                    <InvoiceHistory />
                                </div>
                            </Col>
                        )}
                    </Row>
                </>
            )}
        </>
    );
}

type PlanSettingsFooterProps = {
    planCode: string;
    hasActiveSubscription: boolean;
    isLoading?: boolean;
    isThisPlanSelected?: boolean;
    isInFreeTrialSetup?: boolean;
    onPlanClicked: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
};

function PlanSettingsFooter({ planCode, isThisPlanSelected, hasActiveSubscription, isInFreeTrialSetup, isLoading, onPlanClicked }: PlanSettingsFooterProps) {
    const planAction = hasActiveSubscription ? (isThisPlanSelected ? 'cancel' : 'update') : 'checkout';
    const buttonLabel = hasActiveSubscription ? (isThisPlanSelected ? 'Cancel subscription' : 'Update subscription') : 'Checkout';

    return (
        <>
            {isInFreeTrialSetup && (
                <Button
                    className={isThisPlanSelected ? 'disabled' : ''}
                    type="primary"
                    block
                    onClick={onPlanClicked}
                    data-plan-code={planCode}
                    data-plan-action="select"
                >
                    {isThisPlanSelected ? 'Selected plan' : 'Free trial'}
                </Button>
            )}
            {!isInFreeTrialSetup && (
                <Button
                    className={planAction === 'cancel' ? 'cancel' : ''}
                    type="primary"
                    block
                    onClick={onPlanClicked}
                    loading={isLoading}
                    data-plan-code={planCode}
                    data-plan-action={planAction}
                >
                    {buttonLabel}
                </Button>
            )}
        </>
    );
}

type PlanSettingsHeaderProps = {
    name: string;
    price: number;
    billingPeriod: string;
};

function PlanSettingsHeader({ name, price, billingPeriod }: PlanSettingsHeaderProps) {
    const getPlanPrice = () => {
        if (billingPeriod === 'yearly') {
            return (price / 12 / 100).toFixed(2);
        }

        return (price / 100).toFixed(2);
    };

    const getPlanDiscountMessage = () => {
        if (billingPeriod === 'yearly') {
            return `billed yearly, 10% off, $${price / 100}/year`;
        }
        return `$${((price * 0.9) / 100).toFixed(2)}/mo billed yearly, save 10%`;
    };

    return (
        <div className="plan-header">
            <div className="plan-name">{name}</div>
            <div className="plan-price">
                <sup>$</sup>
                <span className="value">{getPlanPrice()}</span>
                <span>/mo</span>
            </div>
            <div className="plan-discount-message">{getPlanDiscountMessage()}</div>
        </div>
    );
}

function PlanSettingsItem({ itemLabel, itemValue }: { itemLabel: string; itemValue: string | number | boolean }) {
    return (
        <List.Item>
            <div className="pricing-item">
                <div className="label">{itemLabel}</div>
                {itemLabel.toLowerCase() === 'white label reports' ? (
                    <div className={`value ${(itemValue as string).toLowerCase() === 'included' ? 'included' : 'not-included'}`}>{itemValue}</div>
                ) : itemLabel.toLowerCase() === 'team members' && itemValue === 'Not included' ? (
                    <div className="value not-included">{itemValue}</div>
                ) : (
                    <div className="value">{itemValue}</div>
                )}
            </div>
        </List.Item>
    );
}
